/**
 * Library page (UX plan §4.1/§4.10, phase U4) — findability across batches
 * (J6): every creative in one filterable grid, batches as re-entry rows with
 * «New batch from this», and the bulk «Plan backlog» flow kept as the
 * secondary action (decision #2).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import TranslateIcon from '@mui/icons-material/Translate';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { SDialog, StyledTextField } from '@modules/ui-components';
import { MediaLightbox } from '@modules/ui-components/dialogs/MediaLightbox';
import {
  useLocaleAuthoring,
  useProjectLocales,
  type ProjectLocale,
  type ProjectLocaleGlossary,
  type ProjectLocaleProfile,
} from '@modules/locale-config';
import { useTaskTransition } from '@modules/background-tasks/hooks/useTaskTransition';
import type {
  AdCreativeBrief,
  AdCreativeDestinationRef,
  AdCreativeItem,
} from '@segmently/schema-models/ad-creative';
import type {
  AdCreativeApiClient,
  AdCreativeDestinationCatalogView,
  AdCreativeLocalizationBatchPreflight,
  AdCreativeLocalizationBatchProgress,
  AdCreativeLocalizationBatchRequest,
  AdCreativeLocalizationBatchShardResult,
} from '../../services/AdCreativeApiClient';
import { AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF } from '../../selectors';
import {
  CARD_STATUS_LABELS,
  deriveCardStatus,
  formatBadge,
  isWinner,
  type BatchCardStatus,
} from '../batch/batchModel';
import {
  creativeLocaleHeadline,
  creativeLocaleSearchText,
  findCreativeLocaleKey,
  getCreativeLocaleCoverage,
  getCreativeLocaleStatus,
  isSegmentlyOwnedCreativeItem,
  resolveCreativeLocaleProjection,
} from '../../services/creativeLocaleProjection';
import {
  resolveLocalizedVariationHandoffItem,
  type LocalizedVariationHandoffParseResult,
} from './localizedVariationHandoff';

export const LIBRARY = {
  page: 'ad-creative-library-page',
  back: 'ad-creative-library-back',
  search: 'ad-creative-library-search',
  filterFormat: 'ad-creative-library-filter-format',
  filterStatus: 'ad-creative-library-filter-status',
  filterWinners: 'ad-creative-library-filter-winners',
  filterSourceLocale: 'ad-creative-library-filter-source-locale',
  filterLocale: 'ad-creative-library-filter-locale',
  filterLocaleReadiness: 'ad-creative-library-filter-locale-readiness',
  grid: 'ad-creative-library-grid',
  card: (id: string) => `ad-creative-library-card-${id}`,
  batches: 'ad-creative-library-batches',
  batchRow: (id: string) => `ad-creative-library-batch-${id}`,
  batchOpen: (id: string) => `ad-creative-library-batch-open-${id}`,
  batchDuplicate: (id: string) => `ad-creative-library-batch-duplicate-${id}`,
  planBacklog: 'ad-creative-library-plan-backlog',
  batchSelectMode: 'ad-creative-library-localization-select-mode',
  batchSelectionBar: 'ad-creative-library-localization-selection-bar',
  batchSelectVisible: 'ad-creative-library-localization-select-visible',
  batchClearSelection: 'ad-creative-library-localization-clear-selection',
  batchConfigure: 'ad-creative-library-localization-configure',
  batchItemCheckbox: (id: string) => `ad-creative-library-localization-item-${id}`,
  localizationDialog: 'ad-creative-library-localization-dialog',
  localizationLocales: 'ad-creative-library-localization-locales',
  localizationTextComponent: 'ad-creative-library-localization-component-text',
  localizationImageReuseComponent: 'ad-creative-library-localization-component-image-reuse',
  localizationDestinationComponent: 'ad-creative-library-localization-component-destination',
  localizationDestinationSelect: (locale: string) => `ad-creative-library-localization-destination-${locale}`,
  localizationUnavailableComponent: (component: string) => `ad-creative-library-localization-component-${component}`,
  localizationCostCap: 'ad-creative-library-localization-cost-cap',
  localizationProfile: 'ad-creative-library-localization-profile',
  localizationGlossary: 'ad-creative-library-localization-glossary',
  localizationPreflight: 'ad-creative-library-localization-preflight',
  localizationPreflightAction: 'ad-creative-library-localization-preflight-action',
  localizationCostConfirmation: 'ad-creative-library-localization-cost-confirmation',
  localizationStart: 'ad-creative-library-localization-start',
  localizationProgress: 'ad-creative-library-localization-progress',
  localizationShardResults: 'ad-creative-library-localization-shard-results',
  localizationShardResult: (shardId: string) => `ad-creative-library-localization-shard-${shardId}`,
  localizationLoadMoreShards: 'ad-creative-library-localization-load-more-shards',
  localizationRefresh: 'ad-creative-library-localization-refresh',
  localizationCancel: 'ad-creative-library-localization-cancel',
  localizationResume: 'ad-creative-library-localization-resume',
} as const;

interface AdCreativeLibraryPageProps {
  projectId: string;
  client: AdCreativeApiClient;
  briefs: AdCreativeBrief[];
  items: AdCreativeItem[];
  onBack: () => void;
  onOpenItem: (briefId: string | undefined, itemId: string) => void;
  onOpenBatch: (briefId: string) => void;
  onPlanBacklog: () => void;
  onRefresh: () => void;
  localizedVariationHandoff?: LocalizedVariationHandoffParseResult;
}

export function AdCreativeLibraryPage({
  projectId,
  client,
  briefs,
  items,
  onBack,
  onOpenItem,
  onOpenBatch,
  onPlanBacklog,
  onRefresh,
  localizedVariationHandoff = { status: 'not_requested' },
}: AdCreativeLibraryPageProps): React.ReactElement {
  const { locales: projectLocales, loading: localesLoading } = useProjectLocales();
  const localizedVariationResolution = useMemo(
    () => localizedVariationHandoff.status === 'valid'
      ? resolveLocalizedVariationHandoffItem(localizedVariationHandoff.handoff, items)
      : null,
    [items, localizedVariationHandoff],
  );
  const hasAvailableLocalizedVariation = localizedVariationResolution?.status === 'available';
  const [search, setSearch] = useState('');
  const [formatFilter, setFormatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceLocaleFilter, setSourceLocaleFilter] = useState('all');
  const [localeFilter, setLocaleFilter] = useState(
    hasAvailableLocalizedVariation ? localizedVariationResolution.locale : 'all',
  );
  const [localeReadinessFilter, setLocaleReadinessFilter] = useState('all');
  const [winnersOnly, setWinnersOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [creatingManifestVariation, setCreatingManifestVariation] = useState(false);
  const [focusPinnedItem, setFocusPinnedItem] = useState(hasAvailableLocalizedVariation);
  const [localizationSelectionMode, setLocalizationSelectionMode] = useState(false);
  const [selectedLocalizationItemIds, setSelectedLocalizationItemIds] = useState<string[]>([]);
  const [localizationDialogOpen, setLocalizationDialogOpen] = useState(false);
  // FR1: 🔍 opens the lightbox; card body navigation stays unchanged.
  const [inspectedItem, setInspectedItem] = useState<AdCreativeItem | null>(null);

  const briefTitles = useMemo(
    () => new Map(briefs.map((brief) => [brief.id, brief.title])),
    [briefs],
  );
  const authoredItems = useMemo(() => items.filter(isSegmentlyOwnedCreativeItem), [items]);
  const localizedVariationIdentity = localizedVariationHandoff.status === 'valid'
    ? `${localizedVariationHandoff.handoff.creativeItemId}:${localizedVariationHandoff.handoff.renditionId}:${localizedVariationHandoff.handoff.locale}`
    : localizedVariationHandoff.status;
  const localizedVariationAvailabilityIdentity = localizedVariationResolution?.status === 'available'
    ? `${localizedVariationResolution.item.id}:${localizedVariationResolution.locale}`
    : localizedVariationResolution?.status ?? 'none';

  useEffect(() => {
    if (localizedVariationHandoff.status !== 'valid'
      || localizedVariationResolution?.status !== 'available') {
      setLocaleFilter('all');
      setFocusPinnedItem(false);
      return;
    }
    setLocaleFilter(localizedVariationResolution.locale);
    setFocusPinnedItem(true);
  }, [localizedVariationAvailabilityIdentity, localizedVariationIdentity]);
  const formats = useMemo(() => Array.from(new Set(authoredItems.map(formatBadge))).sort(), [authoredItems]);
  const sourceLocales = useMemo(
    () => Array.from(new Set(authoredItems.map((item) => item.defaultLanguage))).sort(),
    [authoredItems],
  );

  const visibleItems = useMemo(() => authoredItems.filter((item) => {
    if (focusPinnedItem
      && localizedVariationResolution?.status === 'available'
      && item.id !== localizedVariationResolution.item.id) return false;
    if (formatFilter !== 'all' && formatBadge(item) !== formatFilter) return false;
    if (statusFilter !== 'all' && deriveCardStatus(item) !== (statusFilter as BatchCardStatus)) return false;
    if (sourceLocaleFilter !== 'all' && !findCreativeLocaleKey(item, sourceLocaleFilter)) return false;
    if (sourceLocaleFilter !== 'all' && findCreativeLocaleKey(item, sourceLocaleFilter) !== findCreativeLocaleKey(item, item.defaultLanguage)) return false;
    const targetLocale = localeFilter === 'all' ? null : localeFilter;
    if (localeReadinessFilter !== 'all') {
      if (targetLocale) {
        const activeKey = findCreativeLocaleKey(item, targetLocale);
        const localeStatus = activeKey ? getCreativeLocaleStatus(item, activeKey) : 'missing';
        if (!matchesLocaleReadiness(localeStatus, localeReadinessFilter)) return false;
      } else if (getCreativeLocaleCoverage(item).readiness !== localeReadinessFilter) {
        return false;
      }
    } else if (targetLocale && !findCreativeLocaleKey(item, targetLocale)) {
      return false;
    }
    if (winnersOnly && !isWinner(item)) return false;
    if (search.trim()) {
      const haystack = `${creativeLocaleSearchText(item)} ${briefTitles.get(item.briefId ?? '') ?? ''}`.toLowerCase();
      if (!haystack.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  }), [authoredItems, briefTitles, focusPinnedItem, formatFilter, localeFilter, localeReadinessFilter, localizedVariationResolution, search, sourceLocaleFilter, statusFilter, winnersOnly]);
  const selectedLocalizationItems = useMemo(
    () => authoredItems.filter((item) => selectedLocalizationItemIds.includes(item.id)),
    [authoredItems, selectedLocalizationItemIds],
  );

  useEffect(() => {
    const authoredIds = new Set(authoredItems.map((item) => item.id));
    setSelectedLocalizationItemIds((current) => current.filter((id) => authoredIds.has(id)));
  }, [authoredItems]);

  const toggleLocalizationItem = (itemId: string) => {
    setSelectedLocalizationItemIds((current) => current.includes(itemId)
      ? current.filter((id) => id !== itemId)
      : [...current, itemId]);
  };

  const duplicateBatch = async (brief: AdCreativeBrief) => {
    setDuplicating(brief.id);
    setError(null);
    try {
      const briefRecord = brief as unknown as Record<string, unknown>;
      const response = await client.createBrief({
        projectId,
        title: `${brief.title} (new batch)`,
        networkId: brief.networkId,
        objective: brief.objective,
        kpi: brief.kpi,
        audience: brief.audience as Record<string, unknown> | undefined,
        task: brief.task as Record<string, unknown> | undefined,
        destination: brief.destination as unknown as Record<string, unknown> | undefined,
        placementFamilies: brief.placementFamilies,
        productContext: brief.productContext as Record<string, unknown> | undefined,
        sourceRefs: (briefRecord.sourceRefs ?? undefined) as Record<string, unknown> | undefined,
        selectedTemplateIds: brief.selectedTemplateIds,
        designCollectionId: brief.designCollectionId,
        selectedDesignPresetIds: brief.selectedDesignPresetIds,
        variantBudget: brief.variantBudget,
      });
      if (!response.success || !response.data?.brief) {
        throw new Error(response.error || 'Failed to duplicate the batch');
      }
      setStatus('New batch created from the existing one — same audience, offer, formats, and style.');
      onRefresh();
      onOpenBatch(response.data.brief.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setDuplicating(null);
    }
  };

  const createExactManifestVariation = async () => {
    if (localizedVariationHandoff.status !== 'valid'
      || localizedVariationResolution?.status !== 'available') return;
    setCreatingManifestVariation(true);
    setError(null);
    try {
      const handoff = localizedVariationHandoff.handoff;
      const response = await client.createManifestVariationChild({
        projectId,
        sourceCreativeItemId: handoff.creativeItemId,
        sourceExportManifestId: handoff.exportManifestId,
        sourceExportedManifestHash: handoff.exportedManifestHash,
        sourceRenditionId: handoff.renditionId,
        sourceLocale: handoff.locale,
        sourceDeploymentKey: handoff.deploymentKey,
        sourceProvider: handoff.provider,
        sourceProviderAccountId: handoff.providerAccountId,
        sourceProviderResourceName: handoff.providerResourceName,
        sourceProviderMutationAllowed: false,
        sourceReviewRequired: true,
        sourceLocalizationVariantId: handoff.localizationVariantId,
      });
      if (!response.success || !response.data?.item) {
        throw new Error(response.error || 'Failed to create an editable variation from the exact manifest');
      }
      setStatus(response.data.reused
        ? 'The existing editable Segmently variation was reopened. The connected ad provider was not changed.'
        : 'Editable Segmently variation created from the exact deployed rendition. It is pending review; the connected ad provider was not changed.');
      onRefresh();
      onOpenItem(response.data.item.briefId, response.data.item.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setCreatingManifestVariation(false);
    }
  };

  return (
    <Stack spacing={2.5} data-testid={LIBRARY.page}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Button size="small" startIcon={<ArrowBackIcon />} data-testid={LIBRARY.back} onClick={onBack}>
          Create
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Library</Typography>
        <Button
          size="small"
          variant={localizationSelectionMode ? 'contained' : 'outlined'}
          startIcon={<TranslateIcon />}
          data-testid={LIBRARY.batchSelectMode}
          onClick={() => {
            setLocalizationSelectionMode((current) => !current);
            if (localizationSelectionMode) setSelectedLocalizationItemIds([]);
          }}
        >
          {localizationSelectionMode ? 'Exit localization selection' : 'Localize copies'}
        </Button>
        <Button
          size="small"
          startIcon={<PlaylistAddIcon />}
          data-testid={LIBRARY.planBacklog}
          onClick={onPlanBacklog}
        >
          Plan backlog (bulk topics)
        </Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {status && <Alert severity="success" onClose={() => setStatus(null)}>{status}</Alert>}

      {localizedVariationHandoff.status === 'invalid' && (
        <Alert
          severity="warning"
          data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.invalid}
        >
          The exact report handoff could not be verified. No creative was opened or changed; the full library remains available.
        </Alert>
      )}

      {localizedVariationHandoff.status === 'valid'
        && localizedVariationResolution?.status === 'unavailable' && (
        <Alert
          severity="warning"
          data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.unavailable}
        >
          {localizedVariationResolution.message} No creative was opened or changed; the full library remains available.
        </Alert>
      )}

      {localizedVariationHandoff.status === 'valid'
        && localizedVariationResolution?.status === 'available' && (
        <Alert
          severity="info"
          data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.banner}
          action={(
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.openSource}
                onClick={() => onOpenItem(
                  localizedVariationResolution.item.briefId,
                  localizedVariationResolution.item.id,
                )}
              >
                Open pinned source for review
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={creatingManifestVariation}
                data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.createChild}
                onClick={() => void createExactManifestVariation()}
              >
                {creatingManifestVariation ? 'Creating…' : 'Create editable variation'}
              </Button>
              {focusPinnedItem && (
                <Button
                  size="small"
                  color="inherit"
                  data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.showAll}
                  onClick={() => setFocusPinnedItem(false)}
                >
                  Show full library
                </Button>
              )}
            </Stack>
          )}
        >
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Review the Segmently source linked to the exact deployed rendition
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.exactPins}
            >
              <Chip size="small" variant="outlined" label={`Locale ${localizedVariationResolution.locale}`} />
              <Chip size="small" variant="outlined" label={`Rendition ${shortPin(localizedVariationHandoff.handoff.renditionId)}`} />
              <Chip size="small" variant="outlined" label={`Deployment ${shortPin(localizedVariationHandoff.handoff.deploymentKey)}`} />
              <Chip size="small" variant="outlined" label={`Manifest ${shortPin(localizedVariationHandoff.handoff.exportManifestId)}`} />
              <Chip size="small" variant="outlined" label={`SHA-256 ${localizedVariationHandoff.handoff.exportedManifestHash.slice(0, 12)}…`} />
            </Stack>
            <Typography
              variant="body2"
              data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.noProviderMutation}
            >
              This is a read-only review handoff. It does not create a child automatically, and Meta or another connected ad provider remains unchanged.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Creating a variation re-resolves the project-owned source, exact manifest, rendition, locale, and deployment binding on the server. The new Segmently copy starts pending review.
            </Typography>
          </Stack>
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <StyledTextField
          label="Search hooks & batches"
          size="small"
          value={search}
          data-testid={LIBRARY.search}
          onChange={(value) => setSearch(String(value))}
          sx={{ minWidth: 220 }}
        />
        <StyledTextField
          select
          label="Format"
          size="small"
          value={formatFilter}
          data-testid={LIBRARY.filterFormat}
          onChange={(value) => setFormatFilter(String(value))}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="all">All formats</MenuItem>
          {formats.map((format) => <MenuItem key={format} value={format}>{format}</MenuItem>)}
        </StyledTextField>
        <StyledTextField
          select
          label="State"
          size="small"
          value={statusFilter}
          data-testid={LIBRARY.filterStatus}
          onChange={(value) => setStatusFilter(String(value))}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All states</MenuItem>
          {(Object.keys(CARD_STATUS_LABELS) as BatchCardStatus[]).map((key) => (
            <MenuItem key={key} value={key}>{CARD_STATUS_LABELS[key]}</MenuItem>
          ))}
        </StyledTextField>
        <StyledTextField
          select
          label="Source language"
          size="small"
          value={sourceLocaleFilter}
          data-testid={LIBRARY.filterSourceLocale}
          onChange={(value) => setSourceLocaleFilter(String(value))}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">All source languages</MenuItem>
          {sourceLocales.map((locale) => (
            <MenuItem key={locale} value={locale}>
              {projectLocales.find((candidate) => candidate.code === locale)?.label ?? locale} · {locale}
            </MenuItem>
          ))}
        </StyledTextField>
        <StyledTextField
          select
          label="Target language"
          size="small"
          value={localeFilter}
          disabled={localesLoading}
          data-testid={LIBRARY.filterLocale}
          onChange={(value) => setLocaleFilter(String(value))}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">All languages</MenuItem>
          {projectLocales.map((locale) => (
            <MenuItem key={locale.code} value={locale.code}>{locale.label} · {locale.code}</MenuItem>
          ))}
        </StyledTextField>
        <StyledTextField
          select
          label="Localization"
          size="small"
          value={localeReadinessFilter}
          data-testid={LIBRARY.filterLocaleReadiness}
          onChange={(value) => setLocaleReadinessFilter(String(value))}
          sx={{ minWidth: 165 }}
        >
          <MenuItem value="all">Any readiness</MenuItem>
          <MenuItem value="ready">Ready</MenuItem>
          <MenuItem value="review_required">Needs review</MenuItem>
          <MenuItem value="missing">Missing</MenuItem>
          <MenuItem value="stale">Stale</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="in_progress">In progress</MenuItem>
        </StyledTextField>
        <Chip
          label="Winners only"
          size="small"
          color={winnersOnly ? 'primary' : 'default'}
          variant={winnersOnly ? 'filled' : 'outlined'}
          data-testid={LIBRARY.filterWinners}
          onClick={() => setWinnersOnly((current) => !current)}
          sx={{ alignSelf: 'center' }}
        />
      </Stack>

      {localizationSelectionMode && (
        <Stack
          data-testid={LIBRARY.batchSelectionBar}
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={1.25}
          sx={{ border: 1, borderColor: 'primary.main', borderRadius: 1.5, p: 1.5, bgcolor: 'action.hover' }}
        >
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Select Segmently copies to localize
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedLocalizationItems.length} selected. Imported Meta/Facebook evidence is not selectable and will not be changed.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              size="small"
              variant="outlined"
              data-testid={LIBRARY.batchSelectVisible}
              disabled={visibleItems.length === 0}
              onClick={() => setSelectedLocalizationItemIds(
                visibleItems.slice(0, 500).map((item) => item.id),
              )}
            >
              Select visible ({Math.min(visibleItems.length, 500)})
            </Button>
            <Button
              size="small"
              data-testid={LIBRARY.batchClearSelection}
              disabled={selectedLocalizationItems.length === 0}
              onClick={() => setSelectedLocalizationItemIds([])}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<TranslateIcon />}
              data-testid={LIBRARY.batchConfigure}
              disabled={selectedLocalizationItems.length === 0}
              onClick={() => setLocalizationDialogOpen(true)}
            >
              Set up localization
            </Button>
          </Stack>
        </Stack>
      )}

      <Box
        data-testid={LIBRARY.grid}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}
      >
        {visibleItems.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
            Nothing matches the filters.
          </Typography>
        )}
        {visibleItems.map((item) => {
          const cardStatus = deriveCardStatus(item);
          const displayLocale = localeFilter === 'all'
            ? item.defaultLanguage
            : findCreativeLocaleKey(item, localeFilter) ?? localeFilter;
          const localeProjection = resolveCreativeLocaleProjection(item, displayLocale, 'preview');
          const coverage = getCreativeLocaleCoverage(item);
          const preview = localeProjection.previewUrl;
          const title = creativeLocaleHeadline(item, displayLocale, 'preview');
          const selectedForLocalization = selectedLocalizationItemIds.includes(item.id);
          return (
            <Box
              key={item.id}
              data-testid={LIBRARY.card(item.id)}
              onClick={() => localizationSelectionMode
                ? toggleLocalizationItem(item.id)
                : onOpenItem(item.briefId, item.id)}
              sx={{
                position: 'relative',
                border: selectedForLocalization
                  || (focusPinnedItem && localizedVariationResolution?.status === 'available' && item.id === localizedVariationResolution.item.id)
                  ? 2
                  : 1,
                borderColor: selectedForLocalization
                  || (focusPinnedItem && localizedVariationResolution?.status === 'available' && item.id === localizedVariationResolution.item.id)
                  ? 'primary.main'
                  : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.light' },
              }}
            >
              {localizationSelectionMode && (
                <Checkbox
                  checked={selectedForLocalization}
                  inputProps={{ 'aria-label': `Select ${title} for localization` }}
                  data-testid={LIBRARY.batchItemCheckbox(item.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleLocalizationItem(item.id)}
                  sx={{
                    position: 'absolute',
                    zIndex: 2,
                    top: 4,
                    left: 4,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    p: 0.5,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                />
              )}
              {preview ? (
                <>
                  {localeProjection.previewMediaKind === 'video' ? (
                    <Box component="video" src={preview} aria-label={title} muted preload="metadata" sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', bgcolor: 'grey.100' }} />
                  ) : (
                    <Box component="img" src={preview} alt={title} loading="lazy" decoding="async" sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', bgcolor: 'grey.100' }} />
                  )}
                  {/* FR1: explicit inspect affordance — card body still navigates. */}
                  <Chip
                    size="small"
                    icon={<ZoomInIcon />}
                    data-testid={`ad-creative-library-card-zoom-${item.id}`}
                    label=""
                    onClick={(event) => {
                      event.stopPropagation();
                      setInspectedItem(item);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      '& .MuiChip-label': { display: 'none' },
                      '& .MuiChip-icon': { color: '#fff', ml: 0, mr: 0 },
                      px: 0.5,
                    }}
                  />
                </>
              ) : (
                <Box sx={{ width: '100%', aspectRatio: '1 / 1', bgcolor: 'grey.100' }} />
              )}
              <Stack spacing={0.5} sx={{ p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap title={title}>
                  {title}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {focusPinnedItem
                    && localizedVariationResolution?.status === 'available'
                    && item.id === localizedVariationResolution.item.id && (
                    <Chip
                      size="small"
                      color="primary"
                      label="Report source"
                      data-testid={AD_CREATIVE_LOCALIZED_VARIATION_HANDOFF.focusedCard}
                    />
                  )}
                  <Chip size="small" variant="outlined" label={formatBadge(item)} />
                  <Chip size="small" variant="outlined" label={CARD_STATUS_LABELS[cardStatus]} />
                  {isWinner(item) && <Chip size="small" color="primary" variant="outlined" label="Winner" />}
                  <Chip
                    size="small"
                    color={coverage.readiness === 'ready' ? 'success' : coverage.readiness === 'failed' ? 'error' : 'default'}
                    variant="outlined"
                    label={`${coverage.ready}/${coverage.active} locales ready`}
                  />
                  <Chip size="small" variant="outlined" label={`${displayLocale} · ${localeProjection.status.replace(/_/g, ' ')}`} />
                  {localeProjection.hasAnyFallback && <Chip size="small" color="info" variant="outlined" label="Source fallback" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {briefTitles.get(item.briefId ?? '') ?? '—'}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Batches</Typography>
      <Stack spacing={1} data-testid={LIBRARY.batches}>
        {briefs.map((brief) => {
          const briefItems = authoredItems.filter((item) => item.briefId === brief.id);
          return (
            <Stack
              key={brief.id}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
              data-testid={LIBRARY.batchRow(brief.id)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}
            >
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    overflow: 'hidden',
                    overflowWrap: 'anywhere',
                    textOverflow: 'ellipsis',
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  }}
                >
                  {brief.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {`${briefItems.length} creatives · ${briefItems.filter(isWinner).length} winners`}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  '& > button': { minWidth: 0 },
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  data-testid={LIBRARY.batchOpen(brief.id)}
                  onClick={() => onOpenBatch(brief.id)}
                  sx={{ flex: { xs: '0 0 auto', sm: 'initial' } }}
                >
                  Open
                </Button>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  data-testid={LIBRARY.batchDuplicate(brief.id)}
                  disabled={duplicating === brief.id}
                  onClick={() => void duplicateBatch(brief)}
                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                >
                  New batch from this
                </Button>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      {localizationDialogOpen && (
        <LocalizationBatchDialog
          projectId={projectId}
          client={client}
          items={selectedLocalizationItems}
          projectLocales={projectLocales}
          onClose={() => setLocalizationDialogOpen(false)}
          onRefresh={onRefresh}
        />
      )}

      {inspectedItem && (
        (() => {
          const displayLocale = localeFilter === 'all'
            ? inspectedItem.defaultLanguage
            : findCreativeLocaleKey(inspectedItem, localeFilter) ?? localeFilter;
          const projection = resolveCreativeLocaleProjection(inspectedItem, displayLocale, 'preview');
          return <MediaLightbox
          open
          onClose={() => setInspectedItem(null)}
          items={[{
            id: inspectedItem.id,
            kind: projection.previewMediaKind === 'video' ? 'video' : 'image',
            src: projection.previewUrl ?? '',
            title: creativeLocaleHeadline(inspectedItem, displayLocale, 'preview'),
            subtitle: `${briefTitles.get(inspectedItem.briefId ?? '') ?? 'Creative'} · ${displayLocale}`,
          }]}
          index={0}
          onIndexChange={() => undefined}
          onOpenDetails={() => {
            setInspectedItem(null);
            onOpenItem(inspectedItem.briefId, inspectedItem.id);
          }}
          />;
        })()
      )}
    </Stack>
  );
}

interface LocalizationBatchDialogProps {
  projectId: string;
  client: AdCreativeApiClient;
  items: AdCreativeItem[];
  projectLocales: ProjectLocale[];
  onClose: () => void;
  onRefresh: () => void;
}

function LocalizationBatchDialog({
  projectId,
  client,
  items,
  projectLocales,
  onClose,
  onRefresh,
}: LocalizationBatchDialogProps): React.ReactElement {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const localeAuthoring = useLocaleAuthoring(projectId);
  const [targetLocales, setTargetLocales] = useState<string[]>([]);
  const [includeText, setIncludeText] = useState(true);
  const [includeImageReuse, setIncludeImageReuse] = useState(false);
  const [includeDestination, setIncludeDestination] = useState(false);
  const [destinationSelections, setDestinationSelections] = useState<
    Record<string, AdCreativeDestinationRef>
  >({});
  const [destinationCatalogs, setDestinationCatalogs] = useState<
    Record<string, AdCreativeDestinationCatalogView>
  >({});
  const [destinationCatalogLoading, setDestinationCatalogLoading] = useState(false);
  const [destinationCatalogError, setDestinationCatalogError] = useState<string | null>(null);
  const [hardCostCapUsd, setHardCostCapUsd] = useState('10');
  const [localeProfileId, setLocaleProfileId] = useState('');
  const [glossaryRef, setGlossaryRef] = useState('');
  const [preflight, setPreflight] = useState<AdCreativeLocalizationBatchPreflight | null>(null);
  const [progress, setProgress] = useState<AdCreativeLocalizationBatchProgress | null>(null);
  const [shardResults, setShardResults] = useState<AdCreativeLocalizationBatchShardResult[]>([]);
  const [nextShardCursor, setNextShardCursor] = useState<string | undefined>();
  const [hasMoreShards, setHasMoreShards] = useState(false);
  const [shardsLoading, setShardsLoading] = useState(false);
  const [costConfirmed, setCostConfirmed] = useState(false);
  const [busy, setBusy] = useState<'preflight' | 'start' | 'refresh' | 'cancel' | 'resume' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestKey] = useState(createLocalizationBatchRequestKey);
  const destinationCatalogSourceItemId = items[0]?.id;
  const sourceLocales = useMemo(
    () => Array.from(new Set(items.map((item) => item.defaultLanguage))).sort(),
    [items],
  );
  const commonSourceLocale = sourceLocales.length === 1 ? sourceLocales[0] : undefined;
  const effectiveProfileTargets = useMemo(
    () => targetLocales.filter((locale) => locale !== commonSourceLocale),
    [commonSourceLocale, targetLocales],
  );
  const compatibleProfiles = useMemo(
    () => commonSourceLocale
      ? localeAuthoring.profiles.filter((profile) => exactResourceMatch(
        profile,
        commonSourceLocale,
        effectiveProfileTargets,
      ))
      : [],
    [commonSourceLocale, effectiveProfileTargets, localeAuthoring.profiles],
  );
  const compatibleGlossaries = useMemo(
    () => commonSourceLocale
      ? localeAuthoring.glossaries.filter((glossary) => exactResourceMatch(
        glossary,
        commonSourceLocale,
        effectiveProfileTargets,
      ))
      : [],
    [commonSourceLocale, effectiveProfileTargets, localeAuthoring.glossaries],
  );
  const parsedHardCap = Number(hardCostCapUsd);
  const imageReuseSelections = useMemo(() => Object.fromEntries(items.map((item) => [
    item.id,
    Object.entries(item.localizedMedia ?? {})
      .filter(([, slot]) => slot.mediaKind === 'image' || slot.mediaKind === 'poster')
      .map(([slotKey]) => slotKey)
      .sort(),
  ])), [items]);
  const imageReuseAvailable = items.length > 0
    && items.every((item) => {
      const slotKeys = imageReuseSelections[item.id] ?? [];
      return slotKeys.length > 0
        && slotKeys.length <= 8
        && slotKeys.every((slotKey) => Boolean(
          item.localizedMedia?.[slotKey]?.translations?.[item.defaultLanguage]?.asset,
        ));
    });
  const components = useMemo<AdCreativeLocalizationBatchRequest['components']>(() => [
    ...(includeText ? ['text' as const] : []),
    ...(includeImageReuse ? ['images' as const] : []),
    ...(includeDestination ? ['destination' as const] : []),
  ], [includeDestination, includeImageReuse, includeText]);
  const destinationConfigurationValid = !includeDestination
    || targetLocales.every((locale) => Boolean(destinationSelections[locale]?.type));
  const configurationValid = items.length > 0
    && targetLocales.length > 0
    && components.length > 0
    && destinationConfigurationValid
    && Number.isFinite(parsedHardCap)
    && parsedHardCap > 0
    && parsedHardCap <= 100_000;

  useEffect(() => {
    if (localeProfileId && !compatibleProfiles.some((profile) => profile.id === localeProfileId)) {
      setLocaleProfileId('');
      setPreflight(null);
      setCostConfirmed(false);
    }
  }, [compatibleProfiles, localeProfileId]);

  useEffect(() => {
    if (glossaryRef && !compatibleGlossaries.some((glossary) => glossary.id === glossaryRef)) {
      setGlossaryRef('');
      setPreflight(null);
      setCostConfirmed(false);
    }
  }, [compatibleGlossaries, glossaryRef]);

  useEffect(() => {
    if (!includeText && (localeProfileId || glossaryRef)) {
      setLocaleProfileId('');
      setGlossaryRef('');
      setPreflight(null);
      setCostConfirmed(false);
    }
  }, [glossaryRef, includeText, localeProfileId]);

  useEffect(() => {
    if (!includeDestination || targetLocales.length === 0 || !destinationCatalogSourceItemId) {
      setDestinationCatalogs({});
      setDestinationCatalogError(null);
      setDestinationCatalogLoading(false);
      return undefined;
    }
    let active = true;
    setDestinationCatalogLoading(true);
    setDestinationCatalogError(null);
    void Promise.all(targetLocales.map(async (locale) => {
      const response = await client.listItemDestinations(
        projectId,
        destinationCatalogSourceItemId,
        locale,
        {
          fallbackApproved: false,
          limit: 100,
        },
      );
      if (!response.success || !response.data?.catalog) {
        throw new Error(response.error || `Failed to load destinations for ${locale}.`);
      }
      return [locale, response.data.catalog] as const;
    })).then((catalogs) => {
      if (!active) return;
      const nextCatalogs = Object.fromEntries(catalogs);
      setDestinationCatalogs(nextCatalogs);
      setDestinationSelections((current) => Object.fromEntries(
        Object.entries(current).filter(([locale, destination]) => {
          if (!targetLocales.includes(locale)) return false;
          if (destination.type === 'none') return true;
          const catalog = nextCatalogs[locale];
          return catalog?.options.some((option) => (
            option.compatible
            && destinationOptionToken(option) === destinationSelectionToken(destination)
          )) ?? false;
        }),
      ));
    }).catch((cause) => {
      if (!active) return;
      setDestinationCatalogs({});
      setDestinationCatalogError(cause instanceof Error ? cause.message : String(cause));
    }).finally(() => {
      if (active) setDestinationCatalogLoading(false);
    });
    return () => { active = false; };
  }, [client, destinationCatalogSourceItemId, includeDestination, projectId, targetLocales]);

  const buildRequest = useCallback((): AdCreativeLocalizationBatchRequest => ({
    projectId,
    creativeItemIds: items.map((item) => item.id).sort(),
    targetLocales: [...targetLocales].sort(),
    components,
    ...(includeImageReuse ? { imageReuseSelections } : {}),
    ...(includeDestination ? {
      destinationSelections: Object.fromEntries(
        [...targetLocales]
          .sort()
          .map((locale) => [locale, destinationSelections[locale]]),
      ),
    } : {}),
    hardCostCapUsd: parsedHardCap,
    localeProfileId: includeText ? localeProfileId || undefined : undefined,
    glossaryRef: includeText ? glossaryRef || undefined : undefined,
    requestKey,
  }), [
    components,
    destinationSelections,
    glossaryRef,
    includeDestination,
    includeImageReuse,
    includeText,
    imageReuseSelections,
    items,
    localeProfileId,
    parsedHardCap,
    projectId,
    requestKey,
    targetLocales,
  ]);

  const loadShardResults = useCallback(async (
    batchId: string,
    cursor?: string,
  ) => {
    setShardsLoading(true);
    try {
      const response = await client.listLocalizationBatchShardResults(projectId, batchId, {
        limit: 50,
        cursor,
      });
      if (!response.success || !response.data?.page) {
        throw new Error(response.error || 'Failed to load localization shard results.');
      }
      setShardResults((current) => cursor
        ? mergeShardResults(current, response.data!.page.results)
        : response.data!.page.results);
      setNextShardCursor(response.data.page.nextCursor);
      setHasMoreShards(response.data.page.hasMore);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setShardsLoading(false);
    }
  }, [client, projectId]);

  const refreshProgress = useCallback(async () => {
    if (!progress?.batchId) return;
    setBusy('refresh');
    try {
      const response = await client.getLocalizationBatch(projectId, progress.batchId);
      if (!response.success || !response.data?.progress) {
        throw new Error(response.error || 'Failed to refresh localization batch progress.');
      }
      setProgress(response.data.progress);
      setError(null);
      if (isLocalizationBatchTerminal(response.data.progress.status)) onRefresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  }, [client, onRefresh, progress?.batchId, projectId]);

  useTaskTransition({
    filter: (task) => Boolean(progress?.taskId && task.id === progress.taskId),
    onTransition: () => void refreshProgress(),
  });

  useEffect(() => {
    if (!progress?.batchId) return;
    void loadShardResults(progress.batchId);
  }, [loadShardResults, progress?.batchId, progress?.updatedAt]);

  useEffect(() => {
    if (!progress || !['pending', 'running', 'cancelling'].includes(progress.status)) return undefined;
    const interval = window.setInterval(() => void refreshProgress(), 60_000);
    return () => window.clearInterval(interval);
  }, [progress?.batchId, progress?.status, refreshProgress]);

  const runPreflight = async () => {
    if (!configurationValid) return;
    setBusy('preflight');
    setError(null);
    setCostConfirmed(false);
    try {
      const response = await client.preflightLocalizationBatch(buildRequest());
      if (!response.success || !response.data?.preflight) {
        throw new Error(response.error || 'Localization batch preflight failed.');
      }
      setPreflight(response.data.preflight);
    } catch (cause) {
      setPreflight(null);
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const startBatch = async () => {
    if (!preflight || !costConfirmed) return;
    setBusy('start');
    setError(null);
    try {
      const response = await client.startLocalizationBatch({
        ...buildRequest(),
        preflightToken: preflight.preflightToken,
      });
      if (!response.success || !response.data?.progress) {
        throw new Error(response.error || 'Failed to start the localization batch.');
      }
      setProgress(response.data.progress);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const cancelBatch = async () => {
    if (!progress?.canCancel) return;
    setBusy('cancel');
    setError(null);
    try {
      const response = await client.cancelLocalizationBatch(projectId, progress.batchId);
      if (!response.success || !response.data?.progress) {
        throw new Error(response.error || 'Failed to cancel the localization batch.');
      }
      setProgress(response.data.progress);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const resumeBatch = async () => {
    if (!progress?.canResume) return;
    setBusy('resume');
    setError(null);
    try {
      const response = await client.resumeLocalizationBatch(projectId, progress.batchId);
      if (!response.success || !response.data?.progress) {
        throw new Error(response.error || 'Failed to resume the localization batch.');
      }
      setProgress(response.data.progress);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const invalidatePreflight = () => {
    setPreflight(null);
    setCostConfirmed(false);
    setError(null);
  };
  const progressActions = progress ? [
    {
      label: 'Refresh progress',
      onClick: () => void refreshProgress(),
      loading: busy === 'refresh',
      disabled: Boolean(busy && busy !== 'refresh'),
      'data-testid': LIBRARY.localizationRefresh,
    },
    ...(progress.canCancel ? [{
      label: 'Cancel remaining work',
      onClick: () => void cancelBatch(),
      color: 'error' as const,
      loading: busy === 'cancel',
      disabled: Boolean(busy && busy !== 'cancel'),
      'data-testid': LIBRARY.localizationCancel,
    }] : []),
    ...(progress.canResume ? [{
      label: 'Resume retryable work',
      onClick: () => void resumeBatch(),
      loading: busy === 'resume',
      disabled: Boolean(busy && busy !== 'resume'),
      'data-testid': LIBRARY.localizationResume,
    }] : []),
  ] : undefined;

  return (
    <SDialog
      open
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      title="Localize selected Segmently copies"
      data-testid={LIBRARY.localizationDialog}
      onClose={onClose}
      actions={progressActions}
      primaryAction={progress ? undefined : preflight ? {
        label: busy === 'start' ? 'Starting…' : 'Start localization',
        onClick: () => void startBatch(),
        loading: busy === 'start',
        disabled: !costConfirmed || Boolean(busy),
        'data-testid': LIBRARY.localizationStart,
      } : {
        label: busy === 'preflight' ? 'Checking…' : 'Review exact preflight',
        onClick: () => void runPreflight(),
        loading: busy === 'preflight',
        disabled: !configurationValid || Boolean(busy),
        'data-testid': LIBRARY.localizationPreflightAction,
      }}
      PaperProps={{ sx: { maxHeight: fullScreen ? '100%' : 'min(900px, 92vh)' } }}
    >
      <Stack spacing={2.25} sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <Alert severity="info">
          This creates review drafts only for {items.length} Segmently-owned {items.length === 1 ? 'copy' : 'copies'}.
          Every locale must be reviewed before export or publishing. Meta/Facebook ads and provider evidence remain unchanged.
        </Alert>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {!progress && (
          <>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Target languages</Typography>
              <Typography variant="caption" color="text.secondary">
                Choose active project locales. A locale that is the source for one copy is excluded only for that copy in preflight.
              </Typography>
              <FormGroup
                data-testid={LIBRARY.localizationLocales}
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 0.25 }}
              >
                {projectLocales.map((locale) => {
                  const sourceForEveryItem = items.every((item) => item.defaultLanguage === locale.code);
                  return (
                    <FormControlLabel
                      key={locale.code}
                      disabled={sourceForEveryItem || Boolean(preflight)}
                      control={(
                        <Checkbox
                          checked={targetLocales.includes(locale.code)}
                          onChange={(_, checked) => {
                            invalidatePreflight();
                            setTargetLocales((current) => checked
                              ? [...current, locale.code]
                              : current.filter((candidate) => candidate !== locale.code));
                            if (!checked) {
                              setDestinationSelections((current) => Object.fromEntries(
                                Object.entries(current).filter(([candidate]) => candidate !== locale.code),
                              ));
                            }
                          }}
                        />
                      )}
                      label={`${locale.label} · ${locale.code}${sourceForEveryItem ? ' (source for every selected copy)' : ''}`}
                    />
                  );
                })}
              </FormGroup>
            </Stack>

            <Divider />

            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Component scope</Typography>
              <FormControlLabel
                data-testid={LIBRARY.localizationTextComponent}
                control={(
                  <Checkbox
                    checked={includeText}
                    disabled={Boolean(preflight)}
                    onChange={(_, checked) => {
                      invalidatePreflight();
                      setIncludeText(checked);
                    }}
                  />
                )}
                label="Text — AI-generated copy enters review"
              />
              <FormControlLabel
                data-testid={LIBRARY.localizationImageReuseComponent}
                control={(
                  <Checkbox
                    checked={includeImageReuse}
                    disabled={Boolean(preflight) || !imageReuseAvailable}
                    onChange={(_, checked) => {
                      invalidatePreflight();
                      setIncludeImageReuse(checked);
                    }}
                  />
                )}
                label="Images — reuse exact source only when inspection proves every selected slot is language-neutral"
              />
              {!imageReuseAvailable && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: { xs: 0, sm: 4 } }}>
                  Every selected copy must have 1–8 image/poster slots with exact source assets. Inspect source images in the creative drawer before preflight.
                </Typography>
              )}
              <FormControlLabel
                data-testid={LIBRARY.localizationUnavailableComponent('video')}
                control={<Checkbox disabled />}
                label="Video — unavailable in batch until tier, transcript, voice-rights, and render settings are supported"
              />
              <FormControlLabel
                data-testid={LIBRARY.localizationDestinationComponent}
                control={(
                  <Checkbox
                    checked={includeDestination}
                    disabled={Boolean(preflight)}
                    onChange={(_, checked) => {
                      invalidatePreflight();
                      setIncludeDestination(checked);
                    }}
                  />
                )}
                label="Destination — apply one exact destination per language"
              />
              {includeDestination && (
                <Stack spacing={1} sx={{ pl: { xs: 0, sm: 4 } }}>
                  <Typography variant="caption" color="text.secondary">
                    Nothing is inferred from Meta. Choose an exact compatible project destination or explicitly select no destination for every target language.
                  </Typography>
                  {destinationCatalogError && (
                    <Alert severity="warning">
                      {destinationCatalogError} You can still choose no destination for each language.
                    </Alert>
                  )}
                  {targetLocales.map((locale) => {
                    const catalog = destinationCatalogs[locale];
                    const compatibleOptions = catalog?.options.filter((option) => option.compatible) ?? [];
                    return (
                      <StyledTextField
                        key={locale}
                        select
                        fullWidth
                        size="small"
                        label={`Destination for ${locale}`}
                        value={destinationSelectionToken(destinationSelections[locale])}
                        disabled={Boolean(preflight)}
                        data-testid={LIBRARY.localizationDestinationSelect(locale)}
                        helperText={destinationCatalogLoading && !catalog
                          ? 'Loading published project destinations…'
                          : compatibleOptions.length > 0
                            ? 'Only destinations verified for this exact language are listed.'
                            : 'No compatible published destination was found; choose no destination.'}
                        onChange={(value) => {
                          invalidatePreflight();
                          const token = String(value);
                          const destination = token === 'none'
                            ? { type: 'none' as const }
                            : compatibleOptions.find((option) => destinationOptionToken(option) === token)?.destination;
                          setDestinationSelections((current) => destination
                            ? { ...current, [locale]: destination }
                            : Object.fromEntries(
                              Object.entries(current).filter(([candidate]) => candidate !== locale),
                            ));
                        }}
                      >
                        <MenuItem value="">Choose destination</MenuItem>
                        <MenuItem value="none">No destination</MenuItem>
                        {compatibleOptions.map((option) => (
                          <MenuItem key={destinationOptionToken(option)} value={destinationOptionToken(option)}>
                            {option.kind === 'funnel' ? 'Funnel' : 'Article'} · {option.label}
                          </MenuItem>
                        ))}
                      </StyledTextField>
                    );
                  })}
                </Stack>
              )}
              {components.length === 0 && (
                <Alert severity="warning">Select text, verified language-neutral image reuse, and/or destination.</Alert>
              )}
            </Stack>

            <Divider />

            {includeText && <Stack spacing={1.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Saved localization context</Typography>
              {sourceLocales.length > 1 && (
                <Alert severity="warning">
                  The selection has several source languages ({sourceLocales.join(', ')}). Saved single-source profiles and glossaries are disabled; split the selection by source language to use them.
                </Alert>
              )}
              {localeAuthoring.error && (
                <Alert severity="warning">
                  Saved profiles and glossaries could not be loaded. You can continue without them.
                </Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <StyledTextField
                  select
                  fullWidth
                  size="small"
                  label="Localization profile"
                  value={localeProfileId}
                  disabled={Boolean(preflight) || localeAuthoring.loading || !commonSourceLocale}
                  data-testid={LIBRARY.localizationProfile}
                  helperText={commonSourceLocale
                    ? `Only exact ${commonSourceLocale} → selected-locale matches`
                    : 'Select copies with one source language to use a profile'}
                  onChange={(value) => {
                    invalidatePreflight();
                    const nextId = String(value);
                    setLocaleProfileId(nextId);
                    const linkedGlossary = compatibleProfiles.find((profile) => profile.id === nextId)?.glossaryRef;
                    setGlossaryRef(linkedGlossary && compatibleGlossaries.some((entry) => entry.id === linkedGlossary)
                      ? linkedGlossary
                      : '');
                  }}
                >
                  <MenuItem value="">No saved profile</MenuItem>
                  {compatibleProfiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>{profile.id} · v{profile.version}</MenuItem>
                  ))}
                </StyledTextField>
                <StyledTextField
                  select
                  fullWidth
                  size="small"
                  label="Glossary"
                  value={glossaryRef}
                  disabled={Boolean(preflight) || localeAuthoring.loading || !commonSourceLocale}
                  data-testid={LIBRARY.localizationGlossary}
                  helperText="Only an active exact source/target match is sent"
                  onChange={(value) => {
                    invalidatePreflight();
                    setGlossaryRef(String(value));
                  }}
                >
                  <MenuItem value="">No saved glossary</MenuItem>
                  {compatibleGlossaries.map((glossary) => (
                    <MenuItem key={glossary.id} value={glossary.id}>
                      {glossary.id} · {Object.keys(glossary.entries).length} terms · v{glossary.version}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </Stack>
            </Stack>}

            <StyledTextField
              fullWidth
              size="small"
              type="number"
              label="Hard maximum cost (USD)"
              value={hardCostCapUsd}
              disabled={Boolean(preflight)}
              data-testid={LIBRARY.localizationCostCap}
              helperText="The batch cannot start if the conservative preflight maximum exceeds this cap."
              inputProps={{ min: 0.01, max: 100000, step: 0.01 }}
              onChange={(value) => {
                invalidatePreflight();
                setHardCostCapUsd(String(value));
              }}
            />
          </>
        )}

        {preflight && !progress && (
          <Stack spacing={1.25} data-testid={LIBRARY.localizationPreflight}>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Exact preflight</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${preflight.sourceItems.length} source copies`} />
              <Chip size="small" label={`${preflight.activeTargetLocales.length} active targets`} />
              <Chip size="small" label={`${preflight.shardCount} shards`} />
              <Chip size="small" label={preflight.components.join(' + ')} />
              <Chip size="small" label={`${preflight.exclusions.total} exclusions`} />
            </Stack>
            <Typography variant="body2">
              Targets: {preflight.activeTargetLocales.join(', ')}
            </Typography>
            {preflight.destinationSelections && (
              <Typography variant="body2">
                Destinations: {Object.entries(preflight.destinationSelections)
                  .map(([locale, destination]) => `${locale} → ${destinationLabel(destination)}`)
                  .join(' · ')}
              </Typography>
            )}
            {preflight.imageReuseSelections && (
              <Typography variant="body2">
                Images: {Object.values(preflight.imageReuseSelections).reduce((sum, slots) => sum + slots.length, 0)} exact source slots,
                reuse only, verified language-neutral, no image AI cost.
              </Typography>
            )}
            <Typography variant="body2">
              Estimated cost: {formatUsd(preflight.costEstimate.minimumUsd)}–{formatUsd(preflight.costEstimate.maximumUsd)}.
              Hard cap: {formatUsd(preflight.costEstimate.hardCapUsd)}.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pricing: {preflight.costEstimate.pricingVersions.join(', ')}. Source revisions and saved context versions are pinned by the preflight token.
            </Typography>
            <Stack spacing={0.4} sx={{ maxHeight: 132, overflowY: 'auto', pr: 0.5 }}>
              {preflight.sourceItems.map((source) => (
                <Typography key={source.creativeItemId} variant="caption" color="text.secondary">
                  {source.creativeItemId} · {source.sourceLocale} · revision {shortPin(source.sourceRevision)} · {source.targetLocales.length} targets
                </Typography>
              ))}
            </Stack>
            <FormControlLabel
              data-testid={LIBRARY.localizationCostConfirmation}
              control={(
                <Checkbox
                  checked={costConfirmed}
                  onChange={(_, checked) => setCostConfirmed(checked)}
                />
              )}
              label={`I confirm the ${formatUsd(preflight.costEstimate.hardCapUsd)} hard cap and understand that every output requires review.`}
            />
          </Stack>
        )}

        {progress && (
          <Stack spacing={1.25} data-testid={LIBRARY.localizationProgress}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Batch progress</Typography>
              <Chip
                size="small"
                color={localizationBatchStatusColor(progress.status)}
                label={progress.status.replace(/_/g, ' ')}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={localizationBatchProgressPercent(progress)}
              aria-label="Localization batch progress"
            />
            <Typography variant="body2">
              {progress.progress.completedShards} completed · {progress.progress.failedShards} failed · {progress.progress.pendingShards} pending · {progress.progress.runningShards} running
            </Typography>
            <Typography variant="body2">
              Actual {formatUsd(progress.cost.actualUsd)} · reserved {formatUsd(progress.cost.reservedUsd)} · hard cap {formatUsd(progress.cost.hardCapUsd)}
            </Typography>
            <Stack spacing={0.75} data-testid={LIBRARY.localizationShardResults}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Per-copy locale results</Typography>
              {shardResults.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {shardsLoading
                    ? 'Loading materialized shards…'
                    : 'No shard result has materialized yet. Progress remains checkpointed; refresh after the first delivery.'}
                </Typography>
              )}
              {shardResults.map((shard) => (
                <Stack
                  key={shard.shardId}
                  data-testid={LIBRARY.localizationShardResult(shard.shardId)}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={0.75}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.9 }}
                >
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap title={shard.creativeItemId}>
                      {shard.creativeItemId} · {shard.targetLocale} · {shard.component}
                    </Typography>
                    <Typography variant="caption" color={shard.errorCode ? 'error.main' : 'text.secondary'}>
                      {shard.errorCode
                        ? `${shard.errorCode}${shard.retryable ? ' · retryable' : ''}`
                        : `${shard.disposition ?? 'processing'} · ${shard.attempts} ${shard.attempts === 1 ? 'attempt' : 'attempts'}`}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {formatUsd(shard.actualCostUsd)}
                  </Typography>
                  <Chip
                    size="small"
                    color={shard.status === 'completed' ? 'success' : shard.status === 'failed' ? 'error' : 'info'}
                    variant="outlined"
                    label={shard.status}
                  />
                </Stack>
              ))}
              {hasMoreShards && (
                <Button
                  size="small"
                  variant="outlined"
                  data-testid={LIBRARY.localizationLoadMoreShards}
                  disabled={shardsLoading || !nextShardCursor}
                  onClick={() => progress.batchId && void loadShardResults(progress.batchId, nextShardCursor)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {shardsLoading ? 'Loading…' : 'Load more results'}
                </Button>
              )}
            </Stack>
            {Object.keys(progress.failureCountsByCode).length > 0 && (
              <Alert severity="warning">
                {Object.entries(progress.failureCountsByCode)
                  .map(([code, count]) => `${code}: ${count}`)
                  .join(' · ')}
              </Alert>
            )}
            {progress.cancelRequested && (
              <Alert severity="info">Cancellation was requested. Completed review drafts are preserved.</Alert>
            )}
            {isLocalizationBatchTerminal(progress.status) && (
              <Alert severity={progress.status === 'completed' ? 'success' : 'warning'}>
                {progress.status === 'completed'
                  ? 'All shards finished. Open each locale to review and approve its localized components before export or publishing.'
                  : 'The batch stopped with partial or failed work. Completed locale drafts remain available for review; retry only the resumable work.'}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
              Batch {progress.batchId} · task {progress.taskId}. This workflow changes only Segmently-owned copies and never edits Meta/Facebook ads.
            </Typography>
          </Stack>
        )}
      </Stack>
    </SDialog>
  );
}

function exactResourceMatch(
  resource: ProjectLocaleProfile | ProjectLocaleGlossary,
  sourceLocale: string,
  targetLocales: string[],
): boolean {
  if (resource.status !== 'active' || resource.sourceLocale !== sourceLocale) return false;
  const supported = new Set(resource.targetLocales);
  return targetLocales.every((locale) => supported.has(locale));
}

function destinationOptionToken(
  option: AdCreativeDestinationCatalogView['options'][number],
): string {
  return `${option.kind}:${option.id}`;
}

function destinationSelectionToken(destination?: AdCreativeDestinationRef): string {
  if (!destination?.type) return '';
  if (destination.type === 'none') return 'none';
  if (destination.type === 'article' && destination.articleId) return `article:${destination.articleId}`;
  if (destination.type === 'web_placement' && destination.webPlacementAlias) {
    return `funnel:${destination.webPlacementAlias}`;
  }
  return '';
}

function destinationLabel(destination: AdCreativeDestinationRef): string {
  if (destination.type === 'none') return 'none';
  return destination.label
    || destination.webPlacementAlias
    || destination.articleId
    || destination.url
    || destination.type
    || 'unknown';
}

function createLocalizationBatchRequestKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ? `library_${uuid}` : `library_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function isLocalizationBatchTerminal(status: AdCreativeLocalizationBatchProgress['status']): boolean {
  return ['cancelled', 'completed', 'partial', 'failed'].includes(status);
}

function localizationBatchProgressPercent(progress: AdCreativeLocalizationBatchProgress): number {
  if (progress.progress.totalShards <= 0) return 0;
  return Math.min(
    100,
    Math.round(((progress.progress.completedShards + progress.progress.failedShards)
      / progress.progress.totalShards) * 100),
  );
}

function mergeShardResults(
  current: AdCreativeLocalizationBatchShardResult[],
  next: AdCreativeLocalizationBatchShardResult[],
): AdCreativeLocalizationBatchShardResult[] {
  const byId = new Map(current.map((result) => [result.shardId, result]));
  next.forEach((result) => byId.set(result.shardId, result));
  return Array.from(byId.values()).sort((left, right) => left.ordinal - right.ordinal);
}

function localizationBatchStatusColor(
  status: AdCreativeLocalizationBatchProgress['status'],
): 'default' | 'info' | 'success' | 'warning' | 'error' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'partial' || status === 'cancelled' || status === 'cancelling') return 'warning';
  return 'info';
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: value < 1 ? 4 : 2,
  }).format(value);
}

function matchesLocaleReadiness(status: string, filter: string): boolean {
  if (filter === 'ready') return status === 'ready' || status === 'source';
  if (filter === 'review_required') return ['review_required', 'draft', 'partial'].includes(status);
  if (filter === 'in_progress') return status === 'generating';
  return status === filter;
}

function shortPin(value: string): string {
  return value.length > 24 ? `${value.slice(0, 12)}…${value.slice(-8)}` : value;
}
